import { createSlice } from '@reduxjs/toolkit';

// UI state for the "Ask Reggie" drawer. The drawer is global to the Analytics
// section; individual analytics pages publish what the user is looking at
// (page label, date range, active filters) so answers are anchored to it.
//
// Conversations live here (not in the panel) so closing the drawer keeps the
// thread, and are persisted per user+organization in localStorage by
// RhinoAnalystDrawer. The service stores nothing.

export const MAX_CONVERSATIONS = 20;
export const CONVERSATION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const defaultContext = {
  page: 'Analytics',
  label: 'Analytics',
  filters: {},
  dateRange: null,
};

const initialState = {
  isOpen: false,
  view: 'chat', // chat | history
  context: defaultContext,
  storageKey: null,
  conversations: {}, // id -> { id, title, messages, createdAt, updatedAt, serverConversationId }
  activeConversationId: null,
};

const titleFrom = (text) => {
  const t = (text || '').trim().replace(/\s+/g, ' ');
  return t.length > 70 ? `${t.slice(0, 67)}…` : t;
};

const prune = (conversations) => {
  const cutoff = Date.now() - CONVERSATION_TTL_MS;
  const kept = Object.values(conversations)
    .filter((c) => c && Array.isArray(c.messages) && c.messages.length && (c.updatedAt || 0) >= cutoff)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, MAX_CONVERSATIONS);
  return Object.fromEntries(kept.map((c) => [c.id, c]));
};

// Anything that was mid-flight when the page was last unloaded is finished now.
const settle = (conversation) => ({
  ...conversation,
  messages: (conversation.messages || []).map((m) => ({
    ...m,
    pending: false,
    toolCalls: (m.toolCalls || []).map((t) => (t.status === 'running' ? { ...t, status: 'done' } : t)),
  })),
});

const rhinoanalystSlice = createSlice({
  name: 'RHINOANALYST',
  initialState,
  reducers: {
    openAskReggie: (state) => ({ ...state, isOpen: true }),
    closeAskReggie: (state) => ({ ...state, isOpen: false }),
    toggleAskReggie: (state) => ({ ...state, isOpen: !state.isOpen }),
    setAskReggieView: (state, action) => ({ ...state, view: action.payload === 'history' ? 'history' : 'chat' }),
    setAskReggieContext: (state, action) => ({
      ...state,
      context: { ...defaultContext, ...(action.payload || {}) },
    }),
    clearAskReggieContext: (state) => ({ ...state, context: defaultContext }),

    // Called by the drawer when the (user, org) key changes.
    hydrateConversations: (state, action) => {
      const { storageKey, conversations } = action.payload;
      const restored = prune(
        Object.fromEntries(Object.entries(conversations || {}).map(([id, c]) => [id, settle(c)])),
      );
      return {
        ...state,
        storageKey,
        conversations: restored,
        activeConversationId: null,
        view: 'chat',
      };
    },

    startConversation: (state, action) => {
      const { id, now } = action.payload;
      return {
        ...state,
        view: 'chat',
        activeConversationId: id,
        conversations: {
          ...state.conversations,
          [id]: {
            id, title: '', messages: [], createdAt: now, updatedAt: now, serverConversationId: null,
          },
        },
      };
    },
    newConversation: (state) => ({ ...state, view: 'chat', activeConversationId: null }),
    activateConversation: (state, action) => ({
      ...state,
      view: 'chat',
      activeConversationId: state.conversations[action.payload] ? action.payload : state.activeConversationId,
    }),
    deleteConversation: (state, action) => {
      const { [action.payload]: removed, ...rest } = state.conversations;
      return {
        ...state,
        conversations: rest,
        activeConversationId: state.activeConversationId === action.payload ? null : state.activeConversationId,
      };
    },

    appendMessages: (state, action) => {
      const { id, messages, now } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      const firstUser = messages.find((m) => m.role === 'user');
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            title: c.title || titleFrom(firstUser?.content),
            messages: [...c.messages, ...messages],
            updatedAt: now,
          },
        },
      };
    },
    patchMessage: (state, action) => {
      const {
        id, messageId, patch, appendText,
      } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => (m.id === messageId
              ? { ...m, ...(patch || {}), content: appendText ? `${m.content}${appendText}` : (patch?.content ?? m.content) }
              : m)),
          },
        },
      };
    },
    patchToolCall: (state, action) => {
      const {
        id, messageId, toolCall,
      } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => {
              if (m.id !== messageId) return m;
              const existing = (m.toolCalls || []).some((t) => t.id === toolCall.id);
              const toolCalls = existing
                ? m.toolCalls.map((t) => (t.id === toolCall.id ? { ...t, ...toolCall } : t))
                : [...(m.toolCalls || []), toolCall];
              return { ...m, toolCalls };
            }),
          },
        },
      };
    },
    addAttachment: (state, action) => {
      const { id, messageId, attachment } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => (m.id === messageId
              ? { ...m, attachments: [...(m.attachments || []), attachment] }
              : m)),
          },
        },
      };
    },
    setServerConversationId: (state, action) => {
      const { id, serverConversationId } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return { ...state, conversations: { ...state.conversations, [id]: { ...c, serverConversationId } } };
    },
  },
});

export default rhinoanalystSlice.reducer;

export const {
  openAskReggie,
  closeAskReggie,
  toggleAskReggie,
  setAskReggieView,
  setAskReggieContext,
  clearAskReggieContext,
  hydrateConversations,
  startConversation,
  newConversation,
  activateConversation,
  deleteConversation,
  appendMessages,
  patchMessage,
  patchToolCall,
  addAttachment,
  setServerConversationId,
} = rhinoanalystSlice.actions;

export const getAskReggieState = (state) => state.rhinoanalyst;
export const getActiveConversation = (state) => {
  const { conversations, activeConversationId } = state.rhinoanalyst;
  return activeConversationId ? conversations[activeConversationId] || null : null;
};
export const getConversationList = (state) => Object.values(state.rhinoanalyst.conversations)
  .filter((c) => c.messages.length)
  .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

export const conversationStorageKey = (userId, orgId) => (userId && orgId ? `rhinoanalyst:conversations:${userId}:${orgId}` : null);
